import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Users, Car, MapPin, ArrowRight } from 'lucide-react';
import { useVehicleImages } from '@/hooks/useVehicleImages';
import { useTransfers, TransferRoute } from '@/hooks/useTransfers';
import { SearchResultData } from '@/components/TransferBookingForm';
import sedanImage from '@/assets/sedan-transfer.jpg';
import suvImage from '@/assets/suv-transfer.jpg';
import VIPBadge from '@/components/VIPBadge';

interface VehicleSelectionSectionProps {
  searchData: SearchResultData;
  onClose?: () => void;
}

export const VehicleSelectionSection = ({ searchData, onClose }: VehicleSelectionSectionProps) => {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLDivElement>(null);
  const { getVehicleByPassengers } = useVehicleImages();
  const { calculatePrice } = useTransfers();

  const { route, passengers, tripType, fromName, toName } = searchData;

  useEffect(() => {
    // Scroll to this section when it appears
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleServiceSelect = (serviceType: 'standard' | 'vip') => {
    const price = calculatePrice(route, passengers, serviceType, tripType);
    navigate('/booking', {
      state: {
        from: fromName,
        to: toName,
        passengers,
        tripType,
        serviceType,
        price,
        route,
        promoCode: ''
      }
    });
  };

  const standardVehicle = getVehicleByPassengers('standard', passengers);
  const vipVehicle = getVehicleByPassengers('vip', passengers);

  const standardPrice = calculatePrice(route, passengers, 'standard', tripType);
  const vipPrice = calculatePrice(route, passengers, 'vip', tripType);

  return (
    <section 
      ref={sectionRef}
      className="py-16 md:py-20 bg-background relative"
    >
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10 animate-fade-in">
          <VIPBadge variant="gold" className="mb-4">Choose Your Ride</VIPBadge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-foreground">
            Select Your <span className="text-gradient-gold">Vehicle</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Choose the perfect service for your journey
          </p>
          
          {/* Route Summary */}
          <div className="flex items-center justify-center gap-3 mt-6 text-sm text-muted-foreground">
            <div className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-full">
              <MapPin className="h-4 w-4 text-primary" />
              <span className="font-medium text-foreground">{fromName}</span>
            </div>
            <ArrowRight className="h-4 w-4 text-primary" />
            <div className="flex items-center gap-2 bg-muted/50 px-4 py-2 rounded-full">
              <MapPin className="h-4 w-4 text-secondary" />
              <span className="font-medium text-foreground">{toName}</span>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-3">
            {passengers} {passengers === 1 ? 'passenger' : 'passengers'} • {tripType === 'roundtrip' ? 'Round Trip' : 'One Way'}
          </p>
        </div>

        {/* Vehicle Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Standard Service Card */}
          <div className="group bg-card rounded-2xl border-2 border-border hover:border-primary/50 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden animate-scale-in">
            {/* Image */}
            <div className="relative h-48 overflow-hidden">
              <img 
                src={standardVehicle?.image_url || sedanImage} 
                alt="Standard Transfer" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1">
                <Car className="h-3 w-3 mr-1" />
                Standard
              </Badge>
            </div>

            {/* Content */}
            <div className="p-6">
              <h3 className="text-xl font-bold text-foreground mb-2">
                {standardVehicle?.vehicle_name || 'Comfortable Ride'}
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                {standardVehicle?.description || 'Reliable and comfortable sedan perfect for your airport transfer.'}
              </p>

              {/* Features */}
              {standardVehicle?.features && standardVehicle.features.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {standardVehicle.features.slice(0, 3).map((feature, index) => (
                    <span 
                      key={index}
                      className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                <Users className="h-4 w-4" />
                <span>Up to {standardVehicle?.max_passengers || 3} passengers</span>
              </div>

              {/* Price & Button */}
              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div>
                  <p className="text-sm text-muted-foreground">Total Price</p>
                  <p className="text-3xl font-bold text-primary">
                    ${standardPrice.toFixed(0)}
                  </p>
                </div>
                <Button 
                  onClick={() => handleServiceSelect('standard')}
                  className="bg-gradient-to-r from-primary to-primary/80 hover:opacity-90 text-white shadow-lg group/btn"
                >
                  Select
                  <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </div>

          {/* VIP Service Card */}
          {vipVehicle && (
            <div className="group bg-card rounded-2xl border-2 border-secondary/30 hover:border-secondary shadow-lg hover:shadow-2xl hover:shadow-secondary/10 transition-all duration-300 overflow-hidden animate-scale-in" style={{ animationDelay: '100ms' }}>
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img 
                  src={vipVehicle.image_url || suvImage} 
                  alt="VIP Transfer" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <Badge className="absolute top-4 left-4 bg-gradient-to-r from-secondary to-secondary/80 text-white px-3 py-1">
                  <Crown className="h-3 w-3 mr-1" />
                  VIP Luxury
                </Badge>
                <div className="absolute top-4 right-4">
                  <span className="text-xs bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded-full">
                    Most Popular
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-foreground mb-2">
                  {vipVehicle.vehicle_name || 'Luxury Experience'}
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  {vipVehicle.description || 'Premium SUV with extra amenities for the ultimate VIP experience.'}
                </p>

                {/* Features */}
                {vipVehicle.features && vipVehicle.features.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {vipVehicle.features.slice(0, 3).map((feature, index) => (
                      <span 
                        key={index}
                        className="text-xs bg-secondary/10 px-2 py-1 rounded-full text-secondary"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <Users className="h-4 w-4" />
                  <span>Up to {vipVehicle.max_passengers || 6} passengers</span>
                </div>

                {/* Price & Button */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Price</p>
                    <p className="text-3xl font-bold text-secondary">
                      ${vipPrice.toFixed(0)}
                    </p>
                  </div>
                  <Button 
                    onClick={() => handleServiceSelect('vip')}
                    className="bg-gradient-to-r from-secondary to-secondary/80 hover:opacity-90 text-white shadow-lg shadow-secondary/20 group/btn"
                  >
                    Select VIP
                    <Crown className="ml-2 h-4 w-4 group-hover/btn:scale-110 transition-transform" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Close/Search Again Button */}
        {onClose && (
          <div className="text-center mt-8">
            <Button variant="ghost" onClick={onClose} className="text-muted-foreground hover:text-foreground">
              Search Again
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default VehicleSelectionSection;
